import { Octokit } from '@octokit/rest';
import { useEffect, useRef, useState } from 'react';
import { Modal } from '~/components/ui/Modal';
import * as Sentry from '@sentry/remix';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { userStore } from '~/lib/stores/user';

interface GithubModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitRepo: (repoUrl: string, isLoading: boolean) => void;
  isUploadingToWb: boolean;
  githubCreds: {
    GITHUB_CLIENT_ID: string;
    GITHUB_CLIENT_SECRET: string;
  };
}

const GITHUB_CLIENT_ID = process.env.NEXT_PUBLIC_GITHUB_CLIENT_ID || 'Iv23liXB3i6K1vVy7oys';

const { email } = userStore.get();
const GITHUB_CLIENT_SECRET = process.env.NEXT_PUBLIC_GITHUB_CLIENT_SECRET || '1fcdc35df4756e9d7b8b73f3191e44f9d03a164a';
interface GitHubRepo {
  name: string;
  private: boolean;
  visibility: string;
}

interface GitHubTreeResponse {
  tree: Array<{
    type: string;
    path: string;
    size?: number;
  }>;
}

interface GitHubUser {
  email: string;
  avatar_url: string;
  name?: string;
}

const MAX_TOKENS = 50000;
const CHARS_PER_TOKEN = 5;
const MAX_CHARS = MAX_TOKENS * CHARS_PER_TOKEN;
const IGNORED_FILE_TYPES = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.ico', '.webp', '.bmp', '.tiff'];

const calculateRepoSize = async (owner: string, repo: string, accessToken: string): Promise<number> => {
  try {
    return await Sentry.startSpan(
      {
        name: 'calculateRepoSize.GithubModal',
        op: 'calculateRepoSize.GithubModal',
      },
      async () => {
        const response = await fetch(`https://api.github.com/repos/${owner}/${repo}/git/trees/main?recursive=1`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/vnd.github.v3+json',
          },
        });

        if (!response.ok) {
          const masterResponse = await fetch(
            `https://api.github.com/repos/${owner}/${repo}/git/trees/master?recursive=1`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/vnd.github.v3+json',
              },
            },
          );

          if (!masterResponse.ok) {
            throw new Error('Could not fetch repository contents');
          }

          const data = (await masterResponse.json()) as GitHubTreeResponse;
          return calculateTotalSize(data.tree);
        }

        const data = (await response.json()) as GitHubTreeResponse;
        return calculateTotalSize(data.tree);
      },
    );
  } catch (error) {
    console.error('Error calculating repo size:', error);
    throw error;
  }
};

const calculateTotalSize = (tree: any[]): number => {
  return tree.reduce((total, item) => {
    // Skip directories, ignored file types, node_modules, and hidden files

    const fileName = item.path.split('/').pop();

    if (
      (item.type === 'blob' && !IGNORED_FILE_TYPES.some((ext) => item.path.toLowerCase().endsWith(ext))) ||
      !item.path.includes('node_modules') ||
      !(fileName && fileName.startsWith('.')) ||
      fileName !== 'package-lock.json' ||
      fileName !== 'yarn.lock' ||
      fileName !== 'pnpm-lock.yaml'
    ) {
      return total + (item.size || 0);
    }

    return total;
  }, 0);
};

const MAX_POLL_ATTEMPTS = 15;

const validateRepoUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname === 'github.com' && parsedUrl.pathname.split('/').length >= 3;
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        message: 'Invalid URL format',
        url,
      },
    });
    return false;
  }
};

export default function GithubModal({ isOpen, onClose, onSubmitRepo, isUploadingToWb, githubCreds }: GithubModalProps) {
  const [repoUrl, setRepoUrl] = useState('');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [user, setUser] = useState<GitHubUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const pollInterval = useRef<NodeJS.Timeout>();
  const [isRepoUrlValid, setIsRepoUrlValid] = useState(true);

  const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${githubCreds.GITHUB_CLIENT_ID}&scope=repo`;

  const validateToken = async (token: string): Promise<boolean> => {
    try {
      return await Sentry.startSpan(
        {
          name: 'validateToken.GithubModal',
          op: 'validateToken.GithubModal',
        },
        async () => {
          const response = await fetch('https://api.github.com/user', {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/vnd.github+json',
            },
          });
          return response.ok;
        },
      );
    } catch (error) {
      console.error('Error validating token:', error);

      Sentry.captureException(error, {
        extra: {
          message: 'Error validating GitHub token',
          token: token ? 'token-exists' : 'no-token',
        },
      });

      return false;
    }
  };

  const checkGithubToken = () => {
    const pollCount = { current: 0 };

    const checkForToken = async (): Promise<boolean> => {
      const token = selectionStore.get().githubAccessToken;

      if (token) {
        console.log('[checkGithubToken] Found token');

        // Validate token before setting
        const isValid = await fetchUserDetails(token);

        if (!isValid) {
          console.log('[checkGithubToken] Invalid token');
          selectionStore.setKey('githubAccessToken', '');
          setError('GitHub token is invalid or expired. Please reconnect.');
          setAccessToken(null);
          setUser(null);
          return false;
        }

        setAccessToken(token);

        return true;
      }

      pollCount.current += 1;

      if (pollCount.current >= MAX_POLL_ATTEMPTS) {
        console.log('[checkGithubToken] Max attempts reached');
        return false;
      }

      return false;
    };

    // Set up polling
    const interval = setInterval(async () => {
      const result = await checkForToken();

      if (result) {
        clearInterval(interval);
      }
    }, 3000);

    return interval;
  };

  const fetchUserDetails = async (token: string) => {
    try {
      return await Sentry.startSpan(
        {
          name: 'fetchUserDetails.GithubModal',
          op: 'fetchUserDetails.GithubModal',
        },
        async () => {
          const response = await fetch('https://api.github.com/user', {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/vnd.github+json',
            },
          });

          if (response.ok) {
            const userData = (await response.json()) as GitHubUser;

            // console.log('>>>[fetchUserDetails] userData: ', userData);
            setUser(userData);
            setAccessToken(token);
            return true;
          }

          return false;
        },
      );
    } catch (error) {
      console.error('Error fetching user details:', error);

      Sentry.captureException(error, {
        extra: {
          message: 'Error fetching GitHub user details',
          token: token ? 'token exists' : 'no-token',
        },
      });

      return false;
    }
  };

  const handleGithubLogin = () => {
    setError(''); // Clear any existing errors
    window.open(githubAuthUrl, '_blank');
    pollInterval.current = checkGithubToken();
  };

  // Add token validation on component mount
  useEffect(() => {
    const validateStoredToken = async () => {
      const token = selectionStore.get().githubAccessToken;

      if (token) {
        const isValid = await fetchUserDetails(token);

        if (!isValid) {
          selectionStore.setKey('githubAccessToken', '');
          setError('GitHub token is invalid or expired. Please reconnect.');
          setAccessToken(null);
          setUser(null);
        }
      }
    };

    validateStoredToken();
  }, []);

  useEffect(() => {
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  const extractRepoInfo = (url: string) => {
    try {
      const parsedUrl = new URL(url);
      const parts = parsedUrl.pathname.replace(/^\/|\/$/g, '').split('/');

      if (parts.length < 2) {
        return null;
      }

      const owner = parts[0];
      const repo = parts[1];

      if (!owner || !repo) {
        return null;
      }

      return { owner, repo };
    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          message: 'Error extracting repo info',
          url,
        },
      });
      console.error('>>>[extractRepoInfo] error: ', err);
      return null;
    }
  };
  const validateRepository = async (owner: string, repo: string): Promise<boolean> => {
    const accessToken = selectionStore.get().githubAccessToken;

    try {
      const result = await Sentry.startSpan(
        {
          name: 'validateGithubRepository',
          op: 'validateGithubRepository',
        },
        async () => {
          const response = await fetch(`https://api.github.com/repos/${owner}/${repo}`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: 'application/vnd.github.v3+json',
            },
          });

          if (!response.ok) {
            throw new Error('Repository not found or no access');
          }

          const repoData: GitHubRepo = await response.json();
          return true;
        },
      );
      return result;
    } catch (error) {
      console.error('Error validating repository:', error);

      Sentry.captureException(error, {
        extra: {
          message: 'Error validating GitHub repository',
          owner,
          repo,
        },
      });

      return false;
    }
  };

  useEffect(() => {
    setIsLoading(isUploadingToWb);
    console.log('setIsLoading', isUploadingToWb);
  }, [isUploadingToWb]);

  const handleSubmit = async () => {
    console.log('[github] handleSubmit: ', repoUrl);
    setIsLoading(true);

    if (!repoUrl) {
      setError('Please enter a repository URL');
      setIsLoading(false);
      return;
    }

    try {
      if (!repoUrl.startsWith('https://github.com/')) {
        setError('Please enter a valid GitHub repository URL');
        setIsLoading(false);
        return;
      }

      new URL(repoUrl);

      const repoInfo = extractRepoInfo(repoUrl);

      // console.log('>>>[fetchRepositoryContent] repoInfo: ', repoInfo);

      if (!repoInfo) {
        setError('Invalid repository URL. Please enter a URL in the format: https://github.com/owner/repository');
        setIsLoading(false);
        return;
      }

      const hasAccess = await validateRepository(repoInfo.owner, repoInfo.repo);

      if (!hasAccess) {
        console.log("Repository not found or you don't have access to it. Make sure you have the correct permissions.");
        setError("Repository not found or we don't have access to it. Make sure you have the correct permissions.");
        setIsLoading(false);
        return;
      }

      const token = selectionStore.get().githubAccessToken;

      if (!token) {
        setError('GitHub authentication token not found');
        setIsLoading(false);
        return;
      }

      const totalChars = await calculateRepoSize(repoInfo.owner, repoInfo.repo, token);

      console.log('[github] Repository size:', {
        characters: totalChars.toLocaleString(),
        estimatedTokens: Math.ceil(totalChars / CHARS_PER_TOKEN).toLocaleString(),
        maxAllowed: {
          characters: MAX_CHARS.toLocaleString(),
          tokens: MAX_TOKENS.toLocaleString(),
        },
      });

      if (totalChars > MAX_CHARS) {
        setError(
          `Repository is too large (${Math.ceil(totalChars / CHARS_PER_TOKEN).toLocaleString()} tokens). Maximum size is ${MAX_TOKENS.toLocaleString()} tokens (${(MAX_CHARS / 1000).toFixed(0)}K characters)`,
        );
        setIsLoading(false);
        return;
      }

      onSubmitRepo(repoUrl, isLoading);
      setError('');
      setIsLoading(false);
    } catch (error) {
      console.error('Error processing repository:', error);

      // TODO: shall this error be captured by Sentry?

      Sentry.captureException(new Error('Invalid URL format'), {
        extra: {
          message: 'Invalid Github URL format',
          url: repoUrl,
        },
      });
      setError('Please enter a valid URL in the format: https://github.com/owner/repository');
      setIsLoading(false);
    }
  };

  const handleRepoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setRepoUrl(url);
    setIsRepoUrlValid(validateRepoUrl(url));
  };

  const revokeGithubToken = async (token: string) => {
    try {
      const octokit = new Octokit({
        auth: {
          clientType: 'dualite-test',
          clientId: githubCreds.GITHUB_CLIENT_ID,
          clientSecret: githubCreds.GITHUB_CLIENT_SECRET,
        },
      });

      const res = await octokit.request('DELETE /applications/{client_id}/token', {
        client_id: githubCreds.GITHUB_CLIENT_ID,
        access_token: token,
        headers: {
          'X-GitHub-Api-Version': '2022-11-28',
        },
      });

      console.log('res >>>>', res);

      if (!res.status || res.status !== 204) {
        console.error('Failed to revoke token:', res.data);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to revoke GitHub token:', error);
      return false;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="rounded-lg bg-[#121212] p-6 w-[480px]">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-md font-medium text-white">Import Your Code from GitHub</h2>
          <button onClick={onClose} className="text-white/60 hover:text-white">
            <div className="i-ph:x-bold text-2xl" />
          </button>
        </div>

        {accessToken && user ? (
          <div className="space-y-4">
            <div className="flex items-center">
              <img src={user.avatar_url} alt="Profile" className="w-12 h-12 rounded-full" />
              <div className="flex-1 ml-4">
                <p className="text-[12px] font-medium text-white">{user.email || user.name || 'GitHub User'}</p>
              </div>
              <div className="flex flex-row items-center p-2 text-[#e7e7e7]/70 hover:text-white bg-red-500/20 hover:bg-red-500/30 rounded-md transition-colors">
                <div className="i-ph:sign-out w-4 h-4 text-red-400" />
                <button
                  onClick={async () => {
                    const storedToken = selectionStore.get().githubAccessToken;

                    if (storedToken) {
                      await revokeGithubToken(storedToken);
                    }

                    selectionStore.setKey('githubAccessToken', '');
                    setAccessToken(null);
                    setUser(null);
                  }}
                  className="ml-2 text-red-400 text-sm transition-colors"
                >
                  Disconnect
                </button>
              </div>
            </div>
            <hr className="border-t border-white/10" />
            <div className="space-y-2">
              <label className="block text-[12px] text-white/60 font-medium">Your GitHub Repository Link</label>
              <input
                type="text"
                value={repoUrl}
                onChange={handleRepoUrlChange}
                className={`w-full bg-[#1C1C1C] rounded-lg px-4 py-3 text-[12px] text-white border-0 focus:outline-none focus:ring-1 ${
                  isRepoUrlValid ? 'focus:ring-white/20' : 'focus:ring-red-500'
                }`}
                placeholder="https://github.com/owner/repo"
              />
              {!isRepoUrlValid && <p className="text-sm text-red-500">Invalid GitHub repository URL</p>}
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}
            <div className="flex justify-end space-x-3 text-sm">
              <button className="px-4 py-2 text-white rounded-md hover:bg-white/10" onClick={onClose}>
                Cancel
              </button>
              <button
                className="px-4 py-2 text-white rounded-md bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end hover:brightness-94 disabled:bg-blue-800 disabled:cursor-not-allowed flex items-center justify-center"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  'Submit Repository'
                )}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={handleGithubLogin}
            className="w-full text-white rounded-lg py-2 bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end hover:brightness-94 transition-colors font-medium flex items-center justify-center gap-2 text-sm"
          >
            <div className="i-ph:github-logo text-2xl" />
            Connect with GitHub
          </button>
        )}
      </div>
    </Modal>
  );
}
